import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useStory } from '../context/StoryContext';
import { useAuth } from '../context/AuthContext';

const Pricing = () => {
  const { setSubscriptionTier, state } = useStory();
  const { user } = useAuth();

  const handleUpgrade = async (tier) => {
    // We update local state first to persist across the simulated backend/stripe redirect.
    setSubscriptionTier(tier);
    if (user) {
      import('../services/dbService').then(db => db.updateSubscriptionTier(user.id, tier)).catch(console.error);
    }

    try {
      const paymentLink = tier === 'Storyteller'
        ? (import.meta.env.VITE_STRIPE_STORYTELLER_PAYMENT_LINK || 'https://buy.stripe.com/test_storyteller')
        : (import.meta.env.VITE_STRIPE_WORLDBUILDER_PAYMENT_LINK || 'https://buy.stripe.com/test_worldbuilder');

      // Add a client reference ID if the user is logged in
      const finalUrl = user ? `${paymentLink}?client_reference_id=${user.id}` : paymentLink;

      window.location.href = finalUrl;
    } catch (err) {
       console.error("Redirect to Payment Link failed", err);
       alert(`Successfully upgraded to ${tier}! (Mock mode)`);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-16">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <div className="text-center mb-16">
          <h1 className="text-5xl font-serif font-bold text-gray-900 dark:text-white mb-4">Simple Pricing</h1>
          <p className="text-xl text-gray-900 dark:text-purple-300">Choose the tier that fits your creative journey.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Free Tier */}
          <div className="bg-white dark:bg-purple-900/20 p-8 rounded-3xl border border-gray-100 dark:border-purple-800 flex flex-col">
            <h3 className="text-2xl font-bold text-gray-900 dark:text-purple-50 mb-2">Wanderer</h3>
            <div className="text-4xl font-black text-amber-600 mb-6">Free</div>
            <ul className="space-y-4 mb-8 flex-1">
              <li className="flex items-center space-x-3 text-gray-900 dark:text-purple-300"><SafeIcon icon={FiIcons.FiCheck} className="text-green-500" /><span>Basic AI Storytelling</span></li>
              <li className="flex items-center space-x-3 text-gray-900 dark:text-purple-300"><SafeIcon icon={FiIcons.FiCheck} className="text-green-500" /><span>1 Active Story</span></li>
              <li className="flex items-center space-x-3 text-gray-900 dark:text-purple-300"><SafeIcon icon={FiIcons.FiCheck} className="text-green-500" /><span>Standard Image Gen</span></li>
            </ul>
            <button className="w-full btn-secondary py-3" disabled={state.subscriptionTier === 'Wanderer'}>{state.subscriptionTier === 'Wanderer' ? 'Current Plan' : 'Downgrade'}</button>
          </div>

          {/* Pro Tier */}
          <div className="bg-gray-50 dark:bg-purple-900/40 p-8 rounded-3xl border-2 border-amber-400 shadow-xl relative flex flex-col transform md:-translate-y-4">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-amber-400 text-amber-950 px-4 py-1 rounded-full text-xs font-bold uppercase tracking-wider">Most Popular</div>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-purple-50 mb-2">Storyteller</h3>
            <div className="text-4xl font-black text-amber-600 mb-6">$10<span className="text-lg text-gray-900 dark:text-purple-500 font-normal">/mo</span></div>
            <ul className="space-y-4 mb-8 flex-1">
              <li className="flex items-center space-x-3 text-gray-900 dark:text-purple-300"><SafeIcon icon={FiIcons.FiCheck} className="text-green-500" /><span>Advanced AI Models (Claude 3.7)</span></li>
              <li className="flex items-center space-x-3 text-gray-900 dark:text-purple-300"><SafeIcon icon={FiIcons.FiCheck} className="text-green-500" /><span>Unlimited Stories</span></li>
              <li className="flex items-center space-x-3 text-gray-900 dark:text-purple-300"><SafeIcon icon={FiIcons.FiCheck} className="text-green-500" /><span>Priority Image Generation</span></li>
              <li className="flex items-center space-x-3 text-gray-900 dark:text-purple-300"><SafeIcon icon={FiIcons.FiCheck} className="text-green-500" /><span>Story Export & Cloud Sync</span></li>
            </ul>
            <button onClick={() => handleUpgrade('Storyteller')} className="w-full btn-primary py-3">
              {state.subscriptionTier === 'Storyteller' ? 'Current Plan' : 'Upgrade Now'}
            </button>
          </div>

          {/* Elite Tier */}
          <div className="bg-white dark:bg-purple-900/20 p-8 rounded-3xl border border-gray-100 dark:border-purple-800 flex flex-col">
            <h3 className="text-2xl font-bold text-gray-900 dark:text-purple-50 mb-2">Worldbuilder</h3>
            <div className="text-4xl font-black text-amber-600 mb-6">$25<span className="text-lg text-gray-900 dark:text-purple-500 font-normal">/mo</span></div>
            <ul className="space-y-4 mb-8 flex-1">
              <li className="flex items-center space-x-3 text-gray-900 dark:text-purple-300"><SafeIcon icon={FiIcons.FiCheck} className="text-green-500" /><span>Everything in Storyteller</span></li>
              <li className="flex items-center space-x-3 text-gray-900 dark:text-purple-300"><SafeIcon icon={FiIcons.FiCheck} className="text-green-500" /><span>Collaborative Multiplayer</span></li>
              <li className="flex items-center space-x-3 text-gray-900 dark:text-purple-300"><SafeIcon icon={FiIcons.FiCheck} className="text-green-500" /><span>Custom API Key Access</span></li>
              <li className="flex items-center space-x-3 text-gray-900 dark:text-purple-300"><SafeIcon icon={FiIcons.FiCheck} className="text-green-500" /><span>Dedicated Support</span></li>
            </ul>
            <button onClick={() => handleUpgrade('Worldbuilder')} className="w-full btn-secondary py-3">
              {state.subscriptionTier === 'Worldbuilder' ? 'Current Plan' : 'Upgrade Now'}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Pricing;
